import { z } from "zod";
import { ulidSchema } from "./schemas.js";
export const familyCreativeSessionContract = "FAMILY_CREATIVE_BOOK_DRAFT_V1";
export const familyCreativeSessionTruthBoundary = "SAVED_CREATIVE_DECISIONS_MAY_GUIDE_GENERATION_NOT_SOURCE_MANUSCRIPT_RENDER_REVIEW_APPROVAL_OR_FULFILLMENT_AUTHORITY";
export const legacyFamilyCreativeSessionTruthBoundary = "SAVED_CREATIVE_DECISIONS_NOT_SOURCE_MANUSCRIPT_GENERATION_RENDER_REVIEW_APPROVAL_OR_FULFILLMENT_AUTHORITY";
export function familyCreativeSourceFingerprintV1(input) {
    const normalizedText = input.text.replace(/\r\n?/gu, "\n").trim();
    const identity = `${input.contentItemId}\u0000${input.revision}\u0000${input.title.trim()}\u0000${normalizedText}`;
    let hash = 2166136261;
    for (let index = 0; index < identity.length; index += 1) {
        hash ^= identity.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return `local-fnv1a-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}
export const familyCreativeProductSchema = z.enum(["COMIC", "RECIPE", "MY_STORY", "OUR_STORY"]);
export const familyCreativeProposalKindSchema = z.enum([
    "ISSUE_ENGINE",
    "PAGE_TURN",
    "LETTERING_TONE",
    "HEADNOTE_LENS",
    "COOKING_FLOW",
    "MEDIA_ROLE",
    "SCENE_LENS",
    "CHAPTER_MOVEMENT",
    "REFLECTION",
    "PAIRING_STRUCTURE",
    "DIFFERENCE_TREATMENT",
    "JOINT_REFLECTION",
]);
export const familyCreativeInvalidationSchema = z.enum([
    "EDITORIAL_MAP",
    "SEMANTIC_PREVIEW",
    "VISUAL_DIRECTION",
    "RENDITIONS",
]);
export const familyCreativeSourceBindingSchema = z
    .object({
    contentItemId: z.string().trim().min(1).max(160),
    revision: z.number().int().nonnegative(),
    fingerprint: z.string().regex(/^local-fnv1a-[a-f0-9]{8}$/u),
    fingerprintAlgorithm: z.literal("CLIENT_FNV1A32_WITH_REVISION_V1_NOT_ARTIFACT_CHECKSUM"),
})
    .strict();
export const familyCreativeDecisionSchema = z
    .object({
    id: z.string().trim().min(1).max(500),
    proposalId: z.string().trim().min(1).max(160),
    kind: familyCreativeProposalKindSchema,
    action: z.enum(["KEEP", "EDIT", "SET_ASIDE"]),
    title: z.string().trim().min(1).max(300),
    detail: z.string().trim().min(1).max(2_000),
    sourceFingerprint: familyCreativeSourceBindingSchema.shape.fingerprint,
    invalidates: z.array(familyCreativeInvalidationSchema).max(4),
    proposalOrigin: z.literal("AUTHORED_STARTING_POINT"),
})
    .strict()
    .superRefine((decision, context) => {
    if (new Set(decision.invalidates).size !== decision.invalidates.length)
        context.addIssue({
            code: "custom",
            path: ["invalidates"],
            message: "A decision cannot repeat the same invalidation.",
        });
});
const productKinds = {
    COMIC: new Set(["ISSUE_ENGINE", "PAGE_TURN", "LETTERING_TONE"]),
    RECIPE: new Set(["HEADNOTE_LENS", "COOKING_FLOW", "MEDIA_ROLE"]),
    MY_STORY: new Set(["SCENE_LENS", "CHAPTER_MOVEMENT", "REFLECTION"]),
    OUR_STORY: new Set(["PAIRING_STRUCTURE", "DIFFERENCE_TREATMENT", "JOINT_REFLECTION"]),
};
const decisionCollectionSchema = z
    .object({
    product: familyCreativeProductSchema,
    source: familyCreativeSourceBindingSchema,
    decisions: z.array(familyCreativeDecisionSchema).max(100),
    archivedDecisions: z.array(familyCreativeDecisionSchema).max(500),
    invalidations: z.array(familyCreativeInvalidationSchema).max(4),
})
    .strict()
    .superRefine((value, context) => {
    const all = [...value.decisions, ...value.archivedDecisions];
    if (new Set(all.map((decision) => decision.id)).size !== all.length)
        context.addIssue({
            code: "custom",
            path: ["decisions"],
            message: "Creative decision identifiers must be unique across current and archived history.",
        });
    for (const [index, decision] of value.decisions.entries()) {
        if (!productKinds[value.product].has(decision.kind))
            context.addIssue({
                code: "custom",
                path: ["decisions", index, "kind"],
                message: "The creative decision kind does not belong to this product family.",
            });
        if (decision.sourceFingerprint !== value.source.fingerprint)
            context.addIssue({
                code: "custom",
                path: ["decisions", index, "sourceFingerprint"],
                message: "Current decisions must bind to the exact current source fingerprint.",
            });
    }
    for (const [index, decision] of value.archivedDecisions.entries())
        if (!productKinds[value.product].has(decision.kind))
            context.addIssue({
                code: "custom",
                path: ["archivedDecisions", index, "kind"],
                message: "The archived decision kind does not belong to this product family.",
            });
    if (new Set(value.invalidations).size !== value.invalidations.length)
        context.addIssue({
            code: "custom",
            path: ["invalidations"],
            message: "The saved invalidation set cannot contain duplicates.",
        });
});
export const saveFamilyCreativeSessionCommandSchema = decisionCollectionSchema
    .safeExtend({ expectedSessionRevision: z.number().int().nonnegative() })
    .strict();
export const savedFamilyCreativeSessionSchema = decisionCollectionSchema
    .safeExtend({
    contract: z.literal(familyCreativeSessionContract),
    version: z.literal(1),
    bookId: ulidSchema,
    sessionRevision: z.number().int().positive(),
    persistence: z.literal("SAVED_TO_BOOK_DRAFT"),
    consumerState: z.enum(["NOT_CONSUMED_BY_GENERATION", "GENERATION_DIRECTION_ELIGIBLE"]),
    savedBy: z.string().trim().min(1).max(128),
    savedAt: z.string().datetime(),
    truthBoundary: z.enum([
        familyCreativeSessionTruthBoundary,
        legacyFamilyCreativeSessionTruthBoundary,
    ]),
})
    .strict();
export const familyCreativeSessionLocatorSchema = z
    .object({
    product: familyCreativeProductSchema,
    contentItemId: z.string().trim().min(1).max(160),
})
    .strict();
export const familyCreativeSessionReadResponseSchema = z
    .object({
    data: savedFamilyCreativeSessionSchema.nullable(),
    meta: z
        .object({
        state: z.enum(["NOT_SAVED", "CURRENT", "STALE"]),
        currentSource: familyCreativeSourceBindingSchema,
    })
        .strict(),
})
    .strict();
export const familyCreativeSessionWriteResponseSchema = z
    .object({ data: savedFamilyCreativeSessionSchema })
    .strict();
export const familyCreativeGenerationBindingSchema = z
    .object({
    contract: z.literal("FAMILY_CREATIVE_GENERATION_BINDING_V1"),
    sessionRevision: z.number().int().positive(),
    product: familyCreativeProductSchema,
    source: familyCreativeSourceBindingSchema,
    acceptedDecisionIds: z.array(z.string().trim().min(1).max(500)).min(1).max(100),
    acceptedDecisionDigestSha256: z.string().regex(/^[a-f0-9]{64}$/u),
})
    .strict()
    .superRefine((binding, context) => {
    if (new Set(binding.acceptedDecisionIds).size !== binding.acceptedDecisionIds.length)
        context.addIssue({
            code: "custom",
            path: ["acceptedDecisionIds"],
            message: "Accepted creative-decision identifiers must be unique.",
        });
});
//# sourceMappingURL=family-creative-session.js.map